import { useState, useEffect, useCallback, useRef } from "react";
import { apiRequest, ApiError } from "src/services/apiClient";
import { getCache, setCache, invalidateCache } from "src/services/apiCache";

interface UseFetchResult<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

export function useApi<T>(url: string | null, refreshTrigger = 0): UseFetchResult<T> {
    const apiUrl = !url ? "" : url.startsWith("/api") ? url : `/api${url}`;
    const cached = getCache(apiUrl) as T | null;
    const [data, setData] = useState<T | null>(cached);
    const [loading, setLoading] = useState<boolean>(!!apiUrl && cached === null);
    const [error, setError] = useState<string | null>(null);
    // track whether we've ever received data — background refreshes stay silent
    const hasDataRef = useRef(cached !== null);
    /** False only after this hook's component instance unmounts (not on refreshTrigger churn). */
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const doFetch = useCallback(
        async (bypass: boolean, isStale?: () => boolean) => {
            if (!apiUrl) return;
            const aborted = () => Boolean(isStale?.()) || !isMountedRef.current;
            if (!bypass) {
                const hit = getCache(apiUrl) as T | null;
                if (hit !== null) {
                    if (!aborted()) {
                        setData(hit);
                        setLoading(false);
                    }
                    return;
                }
            }
            // Only show the loading spinner on the very first fetch (no data yet)
            if (!aborted() && !hasDataRef.current) setLoading(true);
            if (!aborted()) setError(null);
            try {
                const res = await apiRequest<T>(apiUrl, { method: "GET" });
                if (aborted()) return;
                setCache(apiUrl, res);
                setData(res);
                hasDataRef.current = true;
            } catch (e) {
                if (aborted()) return;
                console.error(`useApi error [${apiUrl}]:`, e);
                setError(e instanceof ApiError ? e.message : "Failed to load data.");
            } finally {
                if (!aborted()) setLoading(false);
            }
        },
        [apiUrl],
    );

    useEffect(() => {
        let stale = false;
        const isStale = () => stale || !isMountedRef.current;
        // refreshTrigger > 0 means an explicit user-triggered refresh — bypass cache
        void doFetch(refreshTrigger > 0, isStale);
        return () => {
            stale = true;
        };
    }, [doFetch, refreshTrigger]);

    useEffect(() => {
        const handler = (e: Event) => {
            const prefixes = (e as CustomEvent<string[]>).detail;
            const affected = prefixes.some(
                (p) => apiUrl === p || apiUrl.startsWith(p + "/") || apiUrl.startsWith(p + "?"),
            );
            if (affected) void doFetch(true, () => !isMountedRef.current);
        };
        window.addEventListener("cache-invalidated", handler);
        return () => window.removeEventListener("cache-invalidated", handler);
    }, [apiUrl, doFetch]);

    const refetch = useCallback(() => {
        invalidateCache(apiUrl);
        return doFetch(true);
    }, [apiUrl, doFetch]);

    return { data, loading, error, refetch };
}
